// App.js - Complete Production Frontend with Gamification
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Onboarding from './Onboarding';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const FREE_QUESTION_LIMIT = 10;

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  const [authError, setAuthError] = useState('');

  // Free play state
  const [freeQuestionsUsed, setFreeQuestionsUsed] = useState(0);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [deviceId, setDeviceId] = useState(null);

  // Legacy guest state (kept for compatibility)
  const [isGuest, setIsGuest] = useState(false);
  const [guestHelpsUsed, setGuestHelpsUsed] = useState(0);

  // App state
  const [currentView, setCurrentView] = useState('home'); // 'home', 'question', 'stats', 'achievements', 'settings'
  const [selectedYear, setSelectedYear] = useState(3);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [aiSimplicity, setAiSimplicity] = useState(3); // 1=Very Simple, 5=Advanced
  
  // Question state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentDiagram, setCurrentDiagram] = useState(null);
  const [currentIllustration, setCurrentIllustration] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [questionResult, setQuestionResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [aiHint, setAiHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentAttempts, setCurrentAttempts] = useState(0); // PHASE A
  const [showAnswerButton, setShowAnswerButton] = useState(false); // PHASE A
  const [selectedOption, setSelectedOption] = useState(null); // PHASE B: For MC questions
  const [showExplanation, setShowExplanation] = useState(false); // PHASE B: For AI explanation
  const [explanation, setExplanation] = useState(''); // PHASE B: Explanation text

  // Token tracking state
  const [tokenUsage, setTokenUsage] = useState({
    today: 0,
    thisMonth: 0,
    remaining: 5000,
    limit: 5000
  });

  // Chat/Follow-up state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Stats state
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  
  // Celebration state
  const [celebration, setCelebration] = useState(null);

  const years = [1, 2, 3, 4, 5, 6];
  const topics = [
    'Number & Place Value',
    'Addition & Subtraction',
    'Multiplication & Division',
    'Fractions',
    'Measurement',
    'Geometry',
    'Statistics'
  ];

  // Load user from localStorage and initialise fingerprint
  useEffect(() => {
    // Handle Google OAuth redirect (token in URL params)
    const urlParams = new URLSearchParams(window.location.search);
    const googleToken = urlParams.get('token');
    const googleUser = urlParams.get('user');
    const upgraded = urlParams.get('upgraded');
    const error = urlParams.get('error');

    if (googleToken && googleUser) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(googleUser));
        localStorage.setItem('token', googleToken);
        localStorage.setItem('user', JSON.stringify(parsedUser));
        setUser(parsedUser);
        localStorage.setItem('freeQuestionsUsed', '0');
        setFreeQuestionsUsed(0);
        if (!parsedUser.onboardingComplete) setShowOnboarding(true);
        // Clean URL
        window.history.replaceState({}, document.title, '/');
        return;
      } catch (e) {
        console.error('Failed to parse Google user:', e);
      }
    }

    if (upgraded === 'true') {
      window.history.replaceState({}, document.title, '/');
      // Refresh user profile to get new subscription status
      fetchUserProfile();
    }

    if (error) {
      setAuthError('Google sign-in failed. Please try again.');
      window.history.replaceState({}, document.title, '/');
    }

    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedSimplicity = localStorage.getItem('aiSimplicity');
    const savedFreeCount = parseInt(localStorage.getItem('freeQuestionsUsed') || '0');

    if (savedSimplicity) setAiSimplicity(parseInt(savedSimplicity));
    setFreeQuestionsUsed(savedFreeCount);

    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      if (!parsedUser.onboardingComplete) {
        setShowOnboarding(true);
      } else {
        fetchUserProfile();
      }
    }

    generateDeviceId();
  }, []);

  const generateDeviceId = () => {
    // Use stored ID if exists
    let id = localStorage.getItem('deviceId');
    if (!id) {
      // Build fingerprint from stable browser properties
      const components = [
        navigator.userAgent,
        navigator.language,
        window.window.screen.colorDepth,
        window.window.screen.width + 'x' + window.window.screen.height,
        new Date().getTimezoneOffset(),
        navigator.hardwareConcurrency || '',
        navigator.platform || '',
      ].join('|');
      // Simple hash
      let hash = 0;
      for (let i = 0; i < components.length; i++) {
        hash = ((hash << 5) - hash) + components.charCodeAt(i);
        hash |= 0;
      }
      id = 'fp_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
      localStorage.setItem('deviceId', id);
    }
    setDeviceId(id);
    return id;
  };

  // API Calls
  const api = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: { ...headers, ...options.headers }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const data = await api(endpoint, {
        method: 'POST',
        body: JSON.stringify(authForm)
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setAuthForm({ email: '', password: '', name: '' });
      setShowAuthModal(false);
      setShowRegisterPrompt(false);
      localStorage.setItem('freeQuestionsUsed', '0');
      setFreeQuestionsUsed(0);
      if (!data.user.onboardingComplete) {
        // New user - show onboarding, don't fetch profile yet
        setShowOnboarding(true);
      } else {
        // Existing user - fetch fresh profile then go home
        fetchUserProfile();
        setCurrentView('home');
      }
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsGuest(false);
    setCurrentView('home');
    window.scrollTo(0, 0);
  };

  const incrementFreeCount = () => {
    const newCount = freeQuestionsUsed + 1;
    setFreeQuestionsUsed(newCount);
    localStorage.setItem('freeQuestionsUsed', newCount.toString());
    if (newCount >= FREE_QUESTION_LIMIT && !user) {
      setShowRegisterPrompt(true);
    }
  };

  const updateAiSimplicity = (level) => {
    setAiSimplicity(level);
    localStorage.setItem('aiSimplicity', level.toString());
  };

  const backToHome = () => {
    setCurrentView('home');
    setCurrentQuestion(null);
    setQuestionResult(null);
    setUserAnswer('');
    setShowHelp(false);
    setAiHint('');
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await api('/api/user/profile');
      // Merge profile with existing user data to preserve onboardingComplete etc.
      setUser(prev => {
        const merged = { ...prev, ...profile };
        localStorage.setItem('user', JSON.stringify(merged));
        return merged;
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error.message);
      // Don't crash if profile fetch fails - user is still logged in
    }
  };

  const startQuestion = async (topic) => {
    // Check free question limit for non-logged-in users
    if (!user && freeQuestionsUsed >= FREE_QUESTION_LIMIT) {
      setShowRegisterPrompt(true);
      return;
    }

    setLoading(true);
    setSelectedTopic(topic);
    setCurrentView('question');
    setUserAnswer('');
    setQuestionResult(null);
    setShowHelp(false);
    setAiHint('');
    setCurrentAttempts(0);
    setShowAnswerButton(false);
    setSelectedOption(null);
    setShowExplanation(false);
    setExplanation('');

    try {
      // Use public endpoint for non-logged-in users, authenticated for logged-in
      const endpoint = user ? '/api/question/generate' : '/api/question/free';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') && { Authorization: `Bearer ${localStorage.getItem('token')}` })
        },
        body: JSON.stringify({ year: selectedYear, topic })
      });

      if (response.status === 402) {
        // Monthly limit reached
        setShowUpgradePrompt(true);
        setCurrentView('home');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get question');
      }

      const data = await response.json();
      setCurrentQuestion(data);
      setCurrentDiagram(data.diagramSvg || null);
      setCurrentIllustration(data.illustrationUrl || null);
      // Track free question usage for non-logged-in users
      if (!user) incrementFreeCount();
    } catch (error) {
      alert(error.message);
      setCurrentView('home');
    } finally {
      setLoading(false);
    }
  };

  const checkAnswer = async () => {
    // PHASE B: For MC questions, use selected option; for text, use userAnswer
    const answerToCheck = currentQuestion?.options ? selectedOption : userAnswer.trim();
    
    if (!answerToCheck) {
      alert(currentQuestion?.options ? 'Please select an answer!' : 'Please enter an answer!');
      return;
    }

    setLoading(true);

    try {
      const result = await api('/api/question/check', {
        method: 'POST',
        body: JSON.stringify({
          userAnswer: answerToCheck
        })
      });

      setQuestionResult(result);
      
      // PHASE A: Track attempts and show answer button if needed
      setCurrentAttempts(result.attempts);
      setShowAnswerButton(result.showAnswerOption || false);

      if (result.isCorrect || result.correct) {  // Handle both response formats
        // Show celebration
        setCelebration({
          type: 'correct',
          stars: result.starsEarned || result.stars || 0,
          leveledUp: result.leveledUp,
          newAchievements: result.newAchievements || []
        });

        // Update user
        await fetchUserProfile();

        // Clear celebration after 1.5 seconds
        setTimeout(() => {
          setCelebration(null);
        }, 1500);

        // Auto-advance to next question after 1.5 seconds
        setTimeout(() => {
          nextQuestion();
        }, 1500);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // PHASE A: Show correct answer (give up)
  const showCorrectAnswer = async () => {
    if (!window.confirm('Are you sure you want to see the answer? You\'ll get a small reward for trying!')) {
      return;
    }

    setLoading(true);
    try {
      const data = await api('/api/question/give-up', {
        method: 'POST'
      });

      setQuestionResult({
        correct: false,
        isCorrect: false,
        message: data.message,
        xpGained: data.xpGained,
        starsEarned: 0,
        correctAnswer: data.correctAnswer,
        gaveUp: true
      });

      // Show small celebration for persistence
      if (data.xpGained > 0) {
        setCelebration({
          type: 'correct',
          stars: 0
        });
        setTimeout(() => setCelebration(null), 1500);
      }

      if (user) {
        await fetchUserProfile();
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // PHASE B: Get AI explanation of the answer (with caching)
  const getExplanation = async () => {
    setLoading(true);
    setShowExplanation(true);
    setExplanation('');

    try {
      const data = await api('/api/question/explain', {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQuestion.id || currentQuestion.questionId,
          question: currentQuestion.question,
          correctAnswer: questionResult.correctAnswer,
          userAnswer: userAnswer
        })
      });

      if (data.cached) console.log('✅ Using cached explanation');
      setExplanation(data.explanation);

      if (user) await fetchUserProfile();
    } catch (error) {
      setExplanation('Sorry, I couldn\'t generate an explanation right now. Please try again!');
    } finally {
      setLoading(false);
    }
  };

  const getAiHelp = async () => {
    // Non-logged-in users: prompt to register for AI hints
    if (!user) {
      setShowHelp(true);
      setAiHint('🎓 Register free to unlock AI hints from Mrs Bruce! <a href="/api/auth/google" style="color:#667eea;font-weight:700;">Sign in with Google — it&apos;s free!</a>');
      return;
    }

    setShowHelp(true);
    setAiHint('');
    setLoading(true);

    try {
      const simplicityLevels = {
        1: 'very simple language suitable for a young child, using basic words and short sentences',
        2: 'simple language with easy-to-understand explanations',
        3: 'clear explanations at grade level',
        4: 'detailed explanations with some mathematical terminology',
        5: 'advanced explanations using proper mathematical language'
      };
      const simplicityInstruction = simplicityLevels[aiSimplicity] || simplicityLevels[3];

      const data = await api('/api/ai/help', {
        method: 'POST',
        body: JSON.stringify({ 
          question: currentQuestion.question,
          currentAnswer: userAnswer,
          simplicity: aiSimplicity,
          simplicityInstruction
        })
      });

      setAiHint(data.hint);
      setShowHelp(true);
      
      if (data.tokensUsed) {
        setTokenUsage({
          today: data.tokensToday || 0,
          thisMonth: data.tokensThisMonth || 0,
          remaining: data.tokensRemaining || 0,
          limit: data.tokenLimit || 5000
        });
      }
      
      await fetchUserProfile();
    } catch (error) {
      if (error.message.includes('Help limit reached') || error.message.includes('Token limit reached')) {
        setShowHelp(true);
        setAiHint('🎓 You\'ve reached your limit! Upgrade to Premium for unlimited help.');
      } else {
        alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Send chat message for follow-up questions
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    
    // Add user message to chat
    const newMessages = [...chatMessages, { role: 'student', content: userMessage }];
    setChatMessages(newMessages);
    setChatLoading(true);

    try {
      // Convert chat format to Anthropic API format
      const apiMessages = newMessages.map(msg => ({
        role: msg.role === 'student' ? 'user' : 'assistant',
        content: msg.content
      }));

      const data = await api('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: apiMessages,
          question: currentQuestion.question
        })
      });

      // Add teacher response
      setChatMessages([...newMessages, { role: 'teacher', content: data.response }]);
      
      // Update token usage if provided
      if (data.tokensUsed && user) {
        setTokenUsage(prev => ({
          ...prev,
          today: data.tokensToday || prev.today,
          remaining: data.tokensRemaining || prev.remaining
        }));
      }

    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages([...newMessages, { 
        role: 'teacher', 
        content: 'Sorry, I had trouble responding. Please try again!' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const nextQuestion = () => {
    setCurrentQuestion(null);
    setUserAnswer('');
    setQuestionResult(null);
    setShowHelp(false);
    setAiHint('');
    setChatMessages([]);
    setShowChat(false);
    startQuestion(selectedTopic);
  };

  const fetchStats = async () => {
    try {
      const data = await api('/api/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchAchievements = async () => {
    try {
      const data = await api('/api/achievements');
      setAchievements(data.achievements);
    } catch (error) {
      console.error('Failed to fetch achievements:', error);
    }
  };

  useEffect(() => {
    if (user && currentView === 'stats') {
      fetchStats();
    }
  }, [currentView, user]);

  useEffect(() => {
    if (user && currentView === 'achievements') {
      fetchAchievements();
    }
  }, [currentView, user]);

  // ============================================
  // RENDER: Onboarding
  // ============================================
  if (user && showOnboarding) {
    return (
      <Onboarding
        user={user}
        token={localStorage.getItem('token')}
        onComplete={(data) => {
          const updatedUser = { ...user, onboardingComplete: true, role: data.role, yearGroup: data.yearGroup };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          setShowOnboarding(false);
          setCurrentView('home');
          if (data.yearGroup) setSelectedYear(parseInt(data.yearGroup) || 3);
        }}
      />
    );
  }

  // ============================================
  // RENDER: Main App
  // ============================================

  const modalStyles = {
    overlay: { position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16 },
    modal: { background:'white',borderRadius:20,padding:'32px 28px',width:'100%',maxWidth:420,position:'relative',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
    closeBtn: { position:'absolute',top:16,right:16,background:'none',border:'none',fontSize:'1.2rem',cursor:'pointer',color:'#aaa' },
    title: { fontSize:'1.3rem',fontWeight:700,marginBottom:16,color:'#1a1a2e' },
    tabs: { display:'flex',gap:8,marginBottom:20 },
    tab: { flex:1,padding:'8px',borderRadius:8,border:'2px solid #e8e8e8',background:'white',cursor:'pointer',fontWeight:600,fontSize:'0.9rem',color:'#888' },
    tabActive: { borderColor:'#667eea',color:'#667eea',background:'#f0f0ff' },
    input: { padding:'12px 14px',borderRadius:10,border:'2px solid #e8e8e8',fontSize:'0.95rem',outline:'none',width:'100%',boxSizing:'border-box' },
    error: { background:'#fff5f5',border:'1px solid #fed7d7',borderRadius:8,padding:'8px 12px',color:'#c53030',fontSize:'0.85rem' },
    submitBtn: { padding:'13px',borderRadius:10,background:'linear-gradient(135deg,#667eea,#764ba2)',border:'none',color:'white',fontWeight:700,fontSize:'0.95rem',cursor:'pointer',width:'100%' },
  };

  return (
    <div className="app">

      {/* Auth Modal */}
      {showAuthModal && (
        <div style={modalStyles.overlay} onClick={() => setShowAuthModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <button style={modalStyles.closeBtn} onClick={() => setShowAuthModal(false)}>✕</button>
            <h2 style={modalStyles.title}>
              {authMode === 'login' ? 'Welcome back! 👋' : 'Join maths67 🚀'}
            </h2>

            {/* Google Sign In - PRIMARY */}
            <a href="/api/auth/google" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
              padding: '13px', borderRadius: 10, background: 'white',
              border: '2px solid #e8e8e8', color: '#333', fontWeight: 700,
              fontSize: '0.95rem', textDecoration: 'none', marginBottom: 16,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer'
            }}>
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </a>

            <div style={{display:'flex',alignItems:'center',gap:8,margin:'0 0 16px'}}>
              <div style={{flex:1,height:1,background:'#e8e8e8'}}/>
              <span style={{fontSize:'0.8rem',color:'#aaa'}}>or use email</span>
              <div style={{flex:1,height:1,background:'#e8e8e8'}}/>
            </div>

            <div style={modalStyles.tabs}>
              <button style={{...modalStyles.tab, ...(authMode==='register' ? modalStyles.tabActive : {})}} onClick={() => setAuthMode('register')}>Register</button>
              <button style={{...modalStyles.tab, ...(authMode==='login' ? modalStyles.tabActive : {})}} onClick={() => setAuthMode('login')}>Sign In</button>
            </div>
            <form onSubmit={handleAuth} style={{display:'flex',flexDirection:'column',gap:12}}>
              {authMode === 'register' && (
                <input style={modalStyles.input} type="text" placeholder="Your name" value={authForm.name} onChange={e => setAuthForm({...authForm, name: e.target.value})} required />
              )}
              <input style={modalStyles.input} type="email" placeholder="Email address" value={authForm.email} onChange={e => setAuthForm({...authForm, email: e.target.value})} required />
              <input style={modalStyles.input} type="password" placeholder="Password" value={authForm.password} onChange={e => setAuthForm({...authForm, password: e.target.value})} required />
              {authError && <div style={modalStyles.error}>{authError}</div>}
              <button type="submit" style={modalStyles.submitBtn} disabled={loading}>
                {loading ? 'Loading...' : authMode === 'login' ? 'Sign In' : 'Create Free Account!'}
              </button>
            </form>
            {authMode === 'register' && (
              <p style={{fontSize:'0.75rem',color:'#aaa',textAlign:'center',marginTop:12}}>
                10 free questions/month. From £3/month for unlimited. Part of your subscription goes to your school! 🏫
              </p>
            )}
          </div>
        </div>
      )}

      {/* Upgrade Prompt - shown when monthly limit reached */}
      {showUpgradePrompt && user && (
        <div style={modalStyles.overlay}>
          <div style={{...modalStyles.modal, textAlign:'center'}}>
            <div style={{fontSize:'3rem',marginBottom:12}}>🌟</div>
            <h2 style={{fontSize:'1.3rem',fontWeight:700,marginBottom:8,color:'#1a1a2e'}}>You've used your 10 free questions this month!</h2>
            <p style={{color:'#666',fontSize:'0.9rem',marginBottom:8,lineHeight:1.5}}>
              Upgrade to continue practising with unlimited questions and AI hints.
            </p>
            <p style={{color:'#667eea',fontSize:'0.85rem',fontWeight:600,marginBottom:24}}>
              Part of your £3/month goes directly to your school 🏫
            </p>
            <button style={{...modalStyles.submitBtn, marginBottom:12}} onClick={async () => {
              try {
                const data = await api('/api/stripe/create-checkout', { method: 'POST' });
                window.location.href = data.url;
              } catch (e) {
                alert('Payment setup failed: ' + e.message);
              }
            }}>
              Upgrade for £3/month 🚀
            </button>
            <br/>
            <button style={{background:'none',border:'none',color:'#aaa',cursor:'pointer',fontSize:'0.82rem',marginTop:8}}
              onClick={() => setShowUpgradePrompt(false)}>
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Register Prompt - shown after 10 free questions */}
      {showRegisterPrompt && !user && (
        <div style={modalStyles.overlay}>
          <div style={{...modalStyles.modal, textAlign:'center'}}>
            <div style={{fontSize:'3rem',marginBottom:12}}>🎉</div>
            <h2 style={{fontSize:'1.3rem',fontWeight:700,marginBottom:8,color:'#1a1a2e'}}>You've tried {FREE_QUESTION_LIMIT} questions!</h2>
            <p style={{color:'#666',fontSize:'0.9rem',marginBottom:24,lineHeight:1.5}}>
              Register free to keep going, save your progress, and unlock AI hints from Mrs Bruce!
            </p>
            <button style={{...modalStyles.submitBtn, marginBottom:12}} onClick={() => { setAuthMode('register'); setShowRegisterPrompt(false); setShowAuthModal(true); }}>
              Create Free Account 🚀
            </button>
            <br/>
            <button style={{background:'none',border:'none',color:'#aaa',cursor:'pointer',fontSize:'0.82rem',marginTop:8}}
              onClick={() => { setShowRegisterPrompt(false); setFreeQuestionsUsed(0); localStorage.setItem('freeQuestionsUsed','0'); }}>
              Give me 10 more questions first
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="header">
        <div className="header-left">
          <h1 onClick={() => setCurrentView('home')}>maths67</h1>
          <div className="user-info">
            {user ? (
              <>
                <span className="user-name">👋 {user.name}</span>
                <span className="level-badge">Lv.{user.level || 1}</span>
              </>
            ) : (
              <span style={{fontSize:'0.8rem',color:'#888'}}>
                {freeQuestionsUsed < FREE_QUESTION_LIMIT
                  ? `${FREE_QUESTION_LIMIT - freeQuestionsUsed} free questions left`
                  : 'Register to continue free'}
              </span>
            )}
          </div>
        </div>

        <nav className="nav">
          <button className={currentView === 'home' ? 'active' : ''} onClick={backToHome}>🏠 Home</button>
          {user && (
            <>
              <button className={currentView === 'stats' ? 'active' : ''} onClick={() => setCurrentView('stats')}>📊 Stats</button>
              <button className={currentView === 'achievements' ? 'active' : ''} onClick={() => setCurrentView('achievements')}>🏆 Achievements</button>
            </>
          )}
          <button className={currentView === 'settings' ? 'active' : ''} onClick={() => setCurrentView('settings')}>⚙️ Settings</button>
        </nav>

        {user ? (
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        ) : (
          <a href="/api/auth/google" style={{padding:'8px 16px',borderRadius:8,background:'white',border:'2px solid #e8e8e8',color:'#333',fontWeight:700,cursor:'pointer',fontSize:'0.85rem',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:6}}>
            <svg width="14" height="14" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign In
          </a>
        )}
      </header>

      {/* XP Bar - Only on home/stats/achievements, not during questions */}
      {user && currentView !== 'question' && (
        <>
          <div className="xp-bar-container">
            <div className="xp-info">
              <span>⭐ {user.totalStars} stars</span>
              <span>{(user.xp || 0) % 100}/100 XP</span>
            </div>
            <div className="xp-bar">
              <div className="xp-fill" style={{ width: `${(user.xp || 0) % 100}%` }}></div>
            </div>
          </div>

          {/* Token banner removed - saves screen space */}
        </>
      )}

      {/* Celebration Overlay */}
      {celebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            {celebration.type === 'correct' && (
              <>
                <div className="stars-display">
                  {Array.from({ length: celebration.stars }).map((_, i) => (
                    <span key={i} className="star-icon animate-star" style={{ animationDelay: `${i * 0.1}s` }}>
                      ⭐
                    </span>
                  ))}
                </div>
                <h2 className="celebration-text">
                  {celebration.stars === 3 ? 'Perfect!' : celebration.stars === 2 ? 'Great!' : 'Good!'}
                </h2>
                
                {celebration.leveledUp && (
                  <div className="level-up-badge">
                    🎉 Level Up! You're now Level {user.level}!
                  </div>
                )}

                {celebration.newAchievements && celebration.newAchievements.length > 0 && (
                  <div className="new-achievements">
                    {celebration.newAchievements.map(ach => (
                      <div key={ach.id} className="achievement-unlock">
                        <span>{ach.emoji}</span>
                        <p>{ach.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {/* Home View */}
        {currentView === 'home' && !currentQuestion && (
          <div className='home-bg' style={{minHeight:'100vh', backgroundImage:'url(/New_Maths67_Hero.png)', backgroundSize:'contain', backgroundRepeat:'no-repeat', backgroundPosition:'center top', backgroundColor:'#0f172a', margin:'-20px -9999px', padding:'180px 9999px 40px'}}>
            <div className='home-content' style={{maxWidth:860, margin:'0 auto'}}>
            <img src='/New_Maths67_Hero.png' alt='maths67' className='mobile-hero' />

            {/* Streak Display */}
            {user?.streak?.current > 0 && (
              <div className="streak-display">
                <span className="streak-emoji">🔥</span>
                <p>{user.streak.current} day streak! Keep it up!</p>
              </div>
            )}

            {/* Free question counter / Sign in CTA for non-logged in */}
            {!user && (
              <div style={{textAlign:'center', margin:'0 0 16px', padding:'16px', background:'white', borderRadius:16, boxShadow:'0 4px 20px rgba(0,0,0,0.08)'}}>
                <p style={{fontSize:'0.9rem',color:'#555',marginBottom:12}}>
                  🎮 {freeQuestionsUsed < FREE_QUESTION_LIMIT 
                    ? `${FREE_QUESTION_LIMIT - freeQuestionsUsed} free questions left this month` 
                    : 'Sign in to continue practising'}
                </p>
                <a href="/api/auth/google" style={{
                  display:'inline-flex', alignItems:'center', gap:10,
                  padding:'12px 24px', borderRadius:10, background:'white',
                  border:'2px solid #e8e8e8', color:'#333', fontWeight:700,
                  fontSize:'0.95rem', textDecoration:'none', marginBottom:8,
                  boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer'
                }}>
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Sign in with Google — it's free!
                </a>
                <p style={{fontSize:'0.75rem',color:'#aaa',margin:0}}>
                  10 free questions/month • No credit card needed
                </p>
              </div>
            )}

            {/* Year Selector */}
            <section className="year-selector">
              <h2>⭐ Choose Your Year</h2>
              <div className="year-buttons">
                {years.map(year => (
                  <button
                    key={year}
                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => [3,4].includes(year) ? setSelectedYear(year) : null}
                    style={[3,4].includes(year) ? {} : {opacity:0.4, cursor:'not-allowed', filter:'grayscale(1)'}}
                    title={[3,4].includes(year) ? '' : 'Coming soon!'}
                  >
                    Year {year}{![3,4].includes(year) ? ' 🔜' : ''}
                  </button>
                ))}
              </div>
            </section>

            {/* Topic Selector */}
            <section className="topic-selector">
              <h2>Choose a Topic</h2>
              <div className="topic-grid">
                {topics.map(topic => (
                  <button
                    key={topic}
                    className="topic-btn"
                    onClick={() => startQuestion(topic)}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </section>

            {/* AI Help Info */}
            <div className="help-info" style={{marginTop:16}}>
              <div className="help-badge">
                {user ? (
                  <>🎓 Teacher Help: {user.isPremium ? 'Unlimited' : `${(user.aiHelpsLimit || 0) - (user.aiHelpsUsed || 0)} free hints remaining`}</>
                ) : (
                  <a href="/api/auth/google" style={{color:"inherit",textDecoration:"none"}}>🎓 Register free to unlock AI hints from Mrs Bruce! →</a>
                )}
              </div>
            </div>
          </div></div>
        )}

        {/* Question View */}
        {currentView === 'question' && currentQuestion && (
          <div className="question-container">
            <div className="question-header">
              <button className="back-btn" onClick={backToHome}>
                ← Back
              </button>
              <span className="question-topic">{currentQuestion.topic}</span>
            </div>

            <div className="question-card">
              {currentDiagram && (
    <div className="diagram-container" style={{
      marginBottom: '20px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <div dangerouslySetInnerHTML={{ __html: currentDiagram }} />
    </div>
  )}
              {!currentDiagram && currentIllustration && (
                <div style={{textAlign:'center', marginBottom: 20}}>
                  <img 
                    src={currentIllustration} 
                    alt="illustration" 
                    style={{maxWidth: 280, width:'100%', borderRadius: 12, boxShadow:'0 4px 16px rgba(0,0,0,0.08)'}}
                  />
                </div>
              )}
  {/* ADD DIAGRAM HERE - END */}
  
              <div className="question-text">
                {currentQuestion.question}
              </div>

              {!questionResult && (
                <>
                  {/* PHASE B: Multiple Choice Options */}
                  {currentQuestion.options ? (
                    <div className="mc-options">
                      {Object.entries(currentQuestion.options).map(([key, value]) => (
                        value && (
                          <button
                            key={key}
                            className={`mc-option ${selectedOption === value ? 'selected' : ''}`}
                            onClick={() => setSelectedOption(value)}
                            disabled={loading}
                          >
                            <span className="mc-letter">{key}</span>
                            <span className="mc-text">{value}</span>
                          </button>
                        )
                      ))}
                    </div>
                  ) : (
                    /* Text Input for non-MC questions */
                    <div className="answer-input-container">
                      <input
                        type="text"
                        className="answer-input"
                        value={userAnswer}
                        onChange={e => setUserAnswer(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && checkAnswer()}
                        placeholder="Type your answer..."
                        autoFocus
                      />
                      {currentQuestion.unit && (
                        <span className="answer-unit">{currentQuestion.unit}</span>
                      )}
                      {currentQuestion.unit && (
                        <div style={{fontSize:'0.78rem',color:'#888',marginTop:4,textAlign:'center'}}>
                          {currentQuestion.unit === '£' && 'Enter amount in pounds e.g. £2.50'}
                          {currentQuestion.unit === 'p' && 'Enter amount in pence e.g. 250p'}
                          {currentQuestion.unit === 'cm' && 'Enter length in centimetres'}
                          {currentQuestion.unit === 'mm' && 'Enter length in millimetres'}
                          {currentQuestion.unit === 'm' && 'Enter length in metres'}
                          {currentQuestion.unit === 'km' && 'Enter distance in kilometres'}
                          {currentQuestion.unit === 'g' && 'Enter weight in grams'}
                          {currentQuestion.unit === 'kg' && 'Enter weight in kilograms'}
                          {currentQuestion.unit === 'ml' && 'Enter volume in millilitres'}
                          {currentQuestion.unit === 'L' && 'Enter volume in litres'}
                          {currentQuestion.unit === 'degrees' || currentQuestion.unit === '°' ? 'Enter angle in degrees' : ''}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="question-actions">
                    <button 
                      className="check-btn"
                      onClick={checkAnswer}
                      disabled={loading}
                    >
                      Check Answer
                    </button>
                    
                    {showAnswerButton && (
                      <button 
                        className="btn btn-secondary"
                        onClick={showCorrectAnswer}
                        disabled={loading}
                      >
                        💡 Show Answer
                      </button>
                    )}
                    
                    <button 
                      className="help-btn"
                      onClick={getAiHelp}
                      disabled={loading}
                    >
                      🎓 Need Help?
                    </button>
                  </div>
                  
                  {currentAttempts > 0 && !showAnswerButton && (
                    <p className="attempts-info">
                      Attempts: {currentAttempts}
                    </p>
                  )}
                </>
              )}

              {loading && showHelp && !aiHint && (
                <div className="teacher-thinking">
                  <div className="thinking-animation">
                    <div className="teacher-avatar">🤔</div>
                    <div className="thinking-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <p className="thinking-message">Mrs Bruce is thinking...</p>
                  <div className="thinking-timer">
                    <div className="timer-bar"></div>
                  </div>
                </div>
              )}

              {showHelp && aiHint && (
                <div className="ai-hint">
                  <div className="hint-header">💡 Mrs Bruce Says:</div>
                  <div className="hint-content" style={{whiteSpace: 'pre-line', lineHeight: '1.8'}}>
                    <span dangerouslySetInnerHTML={{__html: aiHint}} />
                  </div>
                  
                  <div className="hint-controls">
                    <div className="hint-simplicity">
                      <label>
                        <span>Adjust simplicity:</span>
                        <div className="hint-slider-wrapper">
                          <span className="slider-label-left">Simpler</span>
                          <input
                            type="range"
                            min="1"
                            max="5"
                            value={aiSimplicity}
                            onChange={(e) => updateAiSimplicity(parseInt(e.target.value))}
                            className="hint-simplicity-slider"
                          />
                          <span className="slider-label-right">Advanced</span>
                        </div>
                      </label>
                      <div className="current-level">Level {aiSimplicity}/5</div>
                    </div>
                    <button 
                      className="retry-hint-btn" 
                      onClick={() => {
                        setShowHelp(false);
                        setAiHint('');
                        setChatMessages([]);
                        setShowChat(false);
                        setTimeout(() => getAiHelp(), 100);
                      }}
                      disabled={loading}
                    >
                      🔄 Get Different Hint
                    </button>
                  </div>
                </div>
              )}

              {/* Chat Interface for Follow-up Questions */}
              {showHelp && aiHint && !showChat && user && (
                <button 
                  className="show-chat-btn" 
                  onClick={() => setShowChat(true)}
                >
                  💬 Ask a follow-up question
                </button>
              )}

              {showHelp && aiHint && showChat && (
                <div className="chat-interface">
                  <div className="chat-header">
                    <span>💬 Chat with Mrs Bruce</span>
                    <button className="close-chat" onClick={() => setShowChat(false)}>✕</button>
                  </div>
                  
                  <div className="chat-messages">
                    {chatMessages.map((msg, i) => (
                      <div key={i} className={`chat-message ${msg.role}`}>
                        <span className="message-avatar">
                          {msg.role === 'student' ? '🧑‍🎓' : '🎓'}
                        </span>
                        <span className="message-text">{msg.content}</span>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="chat-message teacher">
                        <span className="message-avatar">🎓</span>
                        <span className="message-text typing">Thinking...</span>
                      </div>
                    )}
                  </div>

                  <div className="chat-input-container">
                    <input
                      type="text"
                      className="chat-input"
                      placeholder="Ask Mrs Bruce a question..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && chatInput.trim() && !chatLoading) {
                          sendChatMessage();
                        }
                      }}
                      disabled={chatLoading || chatMessages.length >= 10}
                    />
                    <button 
                      className="send-chat-btn"
                      onClick={sendChatMessage}
                      disabled={!chatInput.trim() || chatLoading || chatMessages.length >= 10}
                    >
                      Send
                    </button>
                  </div>

                  <div className="chat-info">
                    {chatMessages.length >= 10 ? (
                      <span className="chat-limit-reached">Maximum 5 questions reached</span>
                    ) : (
                      <span>{Math.floor(chatMessages.length / 2)}/5 questions used</span>
                    )}
                  </div>
                </div>
              )}
              {/* After getting help, allow student to try again */}
              {showHelp && aiHint && !questionResult && (
                <button className="retry-btn" onClick={() => {
                  setShowHelp(false);
                  setAiHint('');
                  setShowChat(false);
                  setChatMessages([]);
                }}>
                  ↩️ Try Again with Hint
                </button>
              )}
              {questionResult && (
                <div className={`result ${questionResult.isCorrect || questionResult.correct ? 'correct' : 'incorrect'}`}>
                  {(questionResult.isCorrect || questionResult.correct) ? (
                    <>
                      <h3>✅ Correct!</h3>
                      <p>You earned {questionResult.starsEarned || questionResult.stars || 0} star{(questionResult.starsEarned || questionResult.stars || 0) !== 1 ? 's' : ''}!</p>
                      <p className="auto-advance-message">⏱️ Next question in 3 seconds...</p>
                    </>
                  ) : (
                    <>
                      <h3>{questionResult.gaveUp ? '💡 Answer Revealed' : '❌ Not quite right'}</h3>
                      <p>{questionResult.message}</p>
                      
                      {questionResult.gaveUp && questionResult.correctAnswer && (
                        <div className="correct-answer-reveal">
                          The correct answer was: <strong>{questionResult.correctAnswer}</strong>
                        </div>
                      )}
                      
                      {questionResult.gaveUp && questionResult.xpGained > 0 && (
                        <p className="persistence-reward">+{questionResult.xpGained} XP for trying!</p>
                      )}
                      
                      {/* PHASE B: Explain Answer Button */}
                      {questionResult.gaveUp && !showExplanation && (
                        <button className="btn btn-secondary" onClick={getExplanation} disabled={loading}>
                          📚 Explain This Answer
                        </button>
                      )}
                      
                      {/* PHASE B: Show Explanation */}
                      {showExplanation && explanation && (
                        <div className="ai-explanation">
                          <h4>📚 How to Work This Out:</h4>
                          <div className="explanation-content">
                            <ReactMarkdown>{explanation}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                      
                      {showExplanation && loading && (
                        <p className="loading-explanation">🤔 Generating explanation...</p>
                      )}
                      
                      {!questionResult.gaveUp && (
                        <>
                          <p>Your answer: {userAnswer}</p>
                          <p>Try again, or ask your teacher for a hint!</p>
                          <button className="retry-btn" onClick={() => {
                            setQuestionResult(null);
                            setUserAnswer('');
                          }}>
                            Try Again
                          </button>
                        </>
                      )}
                      
                      {questionResult.gaveUp && (
                        <button className="retry-btn" onClick={() => {
                          setCurrentQuestion(null);
                          setQuestionResult(null);
                          setUserAnswer('');
                          setShowHelp(false);
                          setAiHint('');
                          setShowChat(false);
                          setChatMessages([]);
                          setCurrentAttempts(0);
                          setShowAnswerButton(false);
                          // Stay in same topic and generate new question
                          startQuestion(selectedTopic);
                        }}>
                          Next Question
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats View - Dashboard */}
        {currentView === 'stats' && (
          <div style={{padding:'0 0 40px'}}>
            {!stats ? (
              <div style={{textAlign:'center',padding:60,color:'#888'}}>Loading your stats...</div>
            ) : (
              <>
                <div style={{background:'linear-gradient(135deg,#667eea,#764ba2)',color:'white',padding:'24px 20px',marginBottom:20,borderRadius:16}}>
                  <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16}}>
                    <div style={{width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#fb923c,#f97316)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,fontWeight:900}}>{stats.level}</div>
                    <div>
                      <div style={{fontSize:'1.1rem',fontWeight:700}}>Level {stats.level} Mathematician! 🚀</div>
                      <div style={{fontSize:'0.82rem',color:'#e2e8f0'}}>{stats.xp} XP total</div>
                    </div>
                    <div style={{marginLeft:'auto',textAlign:'right'}}>
                      <div style={{fontSize:'1.4rem',fontWeight:900,color:'#fbbf24'}}>⭐ {stats.totalStars}</div>
                      <div style={{fontSize:'0.75rem',color:'#94a3b8'}}>stars earned</div>
                    </div>
                  </div>
                  <div style={{background:'rgba(255,255,255,0.1)',borderRadius:8,height:10,overflow:'hidden'}}>
                    <div style={{width:`${stats.xpProgress}%`,height:'100%',background:'linear-gradient(90deg,#fbbf24,#fb923c)',borderRadius:8}}/>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:12,marginBottom:20}}>
                  {[
                    {icon:'🎯',value:`${stats.accuracy}%`,label:'Accuracy',color:'#4ade80',bg:'#f0fdf4'},
                    {icon:'📝',value:stats.totalQuestions,label:'Questions Done',color:'#38bdf8',bg:'#f0f9ff'},
                    {icon:'✅',value:stats.correctAnswers,label:'Correct',color:'#a78bfa',bg:'#faf5ff'},
                    {icon:'🔥',value:`${stats.currentStreak}d`,label:'Streak',color:'#fb923c',bg:'#fff7ed'},
                    {icon:'🏆',value:stats.achievements,label:'Achievements',color:'#fbbf24',bg:'#fffbeb'},
                    {icon:'⭐',value:stats.totalStars,label:'Stars',color:'#f472b6',bg:'#fdf2f8'},
                  ].map((kpi,i) => (
                    <div key={i} style={{background:kpi.bg,borderRadius:16,padding:'16px 12px',textAlign:'center',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                      <div style={{fontSize:'1.6rem',marginBottom:4}}>{kpi.icon}</div>
                      <div style={{fontSize:'1.5rem',fontWeight:900,color:kpi.color,lineHeight:1}}>{kpi.value}</div>
                      <div style={{fontSize:'0.72rem',color:'#666',marginTop:4,fontWeight:600}}>{kpi.label}</div>
                    </div>
                  ))}
                </div>
                {stats.weakTopics && stats.weakTopics.length > 0 && (
                  <div style={{background:'#fff5f5',border:'2px solid #fed7d7',borderRadius:16,padding:'16px 20px',marginBottom:20}}>
                    <div style={{fontWeight:700,color:'#c53030',marginBottom:10,fontSize:'0.95rem'}}>⚠️ Areas to Focus On</div>
                    {stats.weakTopics.map((t,i) => (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                        <span style={{flex:1,fontSize:'0.85rem',color:'#333',fontWeight:600}}>{t.topic}</span>
                        <div style={{width:100,background:'#fed7d7',borderRadius:6,height:8,overflow:'hidden'}}>
                          <div style={{width:`${t.accuracy}%`,height:'100%',background:'#f56565',borderRadius:6}}/>
                        </div>
                        <span style={{fontSize:'0.8rem',color:'#c53030',fontWeight:700,width:36,textAlign:'right'}}>{t.accuracy}%</span>
                      </div>
                    ))}
                  </div>
                )}
                {Object.keys(stats.topicStats).length > 0 && (() => {
                  const topicEntries = Object.entries(stats.topicStats);
                  const total = topicEntries.reduce((s,[,d]) => s + d.total, 0);
                  const colors = ['#667eea','#fb923c','#4ade80','#f59e0b','#a78bfa','#38bdf8','#ec4899'];
                  let offset = 0;
                  const slices = topicEntries.map(([topic, data], i) => {
                    const pct = data.total / total;
                    const slice = { topic, pct, color: colors[i % colors.length], offset };
                    offset += pct;
                    return slice;
                  });
                  const polarToXY = (pct, r) => {
                    const angle = pct * 2 * Math.PI - Math.PI / 2;
                    return [50 + r * Math.cos(angle), 50 + r * Math.sin(angle)];
                  };
                  return (
                  <div style={{background:'white',borderRadius:16,padding:'20px',marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                    <div style={{fontWeight:700,color:'#1a1a2e',marginBottom:16,fontSize:'1rem'}}>🥧 Questions by Topic</div>
                    <div style={{display:'flex',alignItems:'center',gap:20,flexWrap:'wrap'}}>
                      <svg viewBox="0 0 100 100" style={{width:140,height:140,flexShrink:0}}>
                        {slices.map((s,i) => {
                          const [x1,y1] = polarToXY(s.offset, 40);
                          const [x2,y2] = polarToXY(s.offset + s.pct, 40);
                          const large = s.pct > 0.5 ? 1 : 0;
                          return (
                            <path key={i}
                              d={`M50,50 L${x1},${y1} A40,40 0 ${large},1 ${x2},${y2} Z`}
                              fill={s.color} stroke="white" strokeWidth="1.5"/>
                          );
                        })}
                        <circle cx="50" cy="50" r="22" fill="white"/>
                        <text x="50" y="47" textAnchor="middle" fontSize="8" fill="#1a1a2e" fontWeight="700">{total}</text>
                        <text x="50" y="56" textAnchor="middle" fontSize="5.5" fill="#888">questions</text>
                      </svg>
                      <div style={{flex:1,minWidth:140}}>
                        {slices.map((s,i) => (
                          <div key={i} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                            <div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/>
                            <span style={{fontSize:'0.75rem',color:'#333',flex:1}}>{s.topic.replace(' & ',' & ').replace('Number - ','').replace('Number - Fractions, Decimals and Percentages','Fractions')}</span>
                            <span style={{fontSize:'0.75rem',fontWeight:700,color:s.color}}>{Math.round(s.pct*100)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })()}
                {Object.keys(stats.topicStats).length > 0 && (
                  <div style={{background:'white',borderRadius:16,padding:'20px',marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                    <div style={{fontWeight:700,color:'#1a1a2e',marginBottom:16,fontSize:'1rem'}}>📊 Topic Breakdown</div>
                    {Object.entries(stats.topicStats).sort((a,b)=>b[1].total-a[1].total).map(([topic, data]) => {
                      const pct = Math.round((data.correct/data.total)*100);
                      const barColor = pct >= 80 ? '#667eea' : pct >= 60 ? '#f59e0b' : '#fb923c';
                      return (
                        <div key={topic} style={{marginBottom:14}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                            <span style={{fontSize:'0.83rem',fontWeight:600,color:'#333'}}>{topic}</span>
                            <span style={{fontSize:'0.8rem',fontWeight:700,color:barColor}}>{pct}% <span style={{color:'#999',fontWeight:400}}>({data.correct}/{data.total})</span></span>
                          </div>
                          <div style={{background:'#f0f0f0',borderRadius:6,height:10,overflow:'hidden'}}>
                            <div style={{width:`${pct}%`,height:'100%',background:barColor,borderRadius:6}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {stats.dailyActivity && stats.dailyActivity.length > 0 && (
                  <div style={{background:'white',borderRadius:16,padding:'20px',marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                    <div style={{fontWeight:700,color:'#1a1a2e',marginBottom:16,fontSize:'1rem'}}>📅 Last 7 Days</div>
                    <div style={{display:'flex',gap:8,alignItems:'flex-end',height:90}}>
                      {stats.dailyActivity.map((day,i) => {
                        const dayPct = parseInt(day.correct)/parseInt(day.total);
                        const barH = Math.min(60,Math.max(16,parseInt(day.total)*8));
                        const dayColor = dayPct >= 0.8 ? '#22c55e' : dayPct >= 0.6 ? '#f59e0b' : '#ef4444';
                        return (
                          <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                            <div style={{fontSize:'0.65rem',color:'#999',fontWeight:600}}>{parseInt(day.total)}</div>
                            <div style={{width:'100%',height:barH,background:dayColor,borderRadius:'4px 4px 0 0'}}/>
                            <div style={{fontSize:'0.6rem',color:'#aaa'}}>{new Date(day.day).toLocaleDateString('en-GB',{weekday:'short'})}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {stats.recentQuestions && stats.recentQuestions.length > 0 && (
                  <div style={{background:'white',borderRadius:16,padding:'20px',marginBottom:20,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                    <div style={{fontWeight:700,color:'#1a1a2e',marginBottom:16,fontSize:'1rem'}}>🕐 Recent Questions</div>
                    {stats.recentQuestions.map((q,i) => (
                      <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 0',borderBottom:i<stats.recentQuestions.length-1?'1px solid #f0f0f0':'none'}}>
                        <span style={{fontSize:'1.2rem'}}>{q.is_correct ? '✅' : '❌'}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'0.82rem',color:'#333',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{q.question}</div>
                          <div style={{fontSize:'0.72rem',color:'#999',marginTop:2}}>{q.topic} - {new Date(q.created_at).toLocaleDateString('en-GB')}</div>
                        </div>
                        {q.stars_earned > 0 && <span style={{fontSize:'0.75rem',color:'#f59e0b',fontWeight:700}}>+{q.stars_earned}⭐</span>}
                      </div>
                    ))}
                  </div>
                )}
                {stats.totalQuestions === 0 && (
                  <div style={{textAlign:'center',padding:'40px 20px',background:'white',borderRadius:16,boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                    <div style={{fontSize:'3rem',marginBottom:12}}>🚀</div>
                    <div style={{fontWeight:700,color:'#1a1a2e',marginBottom:8}}>No questions yet!</div>
                    <div style={{color:'#888',fontSize:'0.9rem',marginBottom:16}}>Start answering questions to see your progress here.</div>
                    <button onClick={backToHome} style={{background:'linear-gradient(135deg,#667eea,#764ba2)',color:'white',border:'none',borderRadius:10,padding:'10px 24px',fontWeight:700,cursor:'pointer'}}>Start Practising</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
                {/* Achievements View */}
        {currentView === 'achievements' && (
          <div className="achievements-container">
            <h2>🏆 Achievements</h2>
            <div className="achievements-grid">
              {achievements.map(ach => (
                <div 
                  key={ach.id} 
                  className={`achievement-card ${ach.unlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="achievement-emoji">{ach.emoji}</div>
                  <div className="achievement-info">
                    <h3>{ach.name}</h3>
                    <p>{ach.description}</p>
                  </div>
                  {ach.unlocked && <div className="unlock-badge">✓</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings View */}
        {currentView === 'settings' && (
          <div className="settings-container">
            <h2>⚙️ Settings</h2>
            
            <div className="settings-section">
              <h3>AI Help Simplicity</h3>
              <p className="settings-description">
                Adjust how simple or advanced the AI explanations are
              </p>
              
              <div className="simplicity-slider-container">
                <div className="slider-labels">
                  <span>Very Simple</span>
                  <span>Grade Level</span>
                  <span>Advanced</span>
                </div>
                
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={aiSimplicity}
                  onChange={(e) => updateAiSimplicity(parseInt(e.target.value))}
                  className="simplicity-slider"
                />
                
                <div className="slider-value">
                  <div className="simplicity-indicators">
                    {[1, 2, 3, 4, 5].map(level => (
                      <div 
                        key={level}
                        className={`indicator ${aiSimplicity === level ? 'active' : ''}`}
                      >
                        {level}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="simplicity-preview">
                <h4>Example at current level:</h4>
                <div className="preview-box">
                  {aiSimplicity === 1 && (
                    <p>💡 "Let's break this into tiny steps! Look at each number. What do you need to find?"</p>
                  )}
                  {aiSimplicity === 2 && (
                    <p>💡 "Here's a tip: Break this problem into smaller parts. What information do you have?"</p>
                  )}
                  {aiSimplicity === 3 && (
                    <p>💡 "Think about what the question is asking. What steps can help you solve it?"</p>
                  )}
                  {aiSimplicity === 4 && (
                    <p>💡 "Consider the mathematical operation needed. How can you apply it to solve this?"</p>
                  )}
                  {aiSimplicity === 5 && (
                    <p>💡 "Analyze the problem systematically. What mathematical principles apply here?"</p>
                  )}
                </div>
              </div>

              <div className="settings-note">
                ℹ️ This setting applies to all teacher hints and is saved automatically
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
